// @ts-check
// Popup do "Conectar com a Meta" no modo demonstração (?mock=1, contrato 08 de leads e WhatsApp, seção 8.1): os mesmos
// estados e textos da página do portal (src/integrations/meta-signup-page.js), inclusive o aperto de mão com a janela
// da escola antes de liberar o login, com uma "Meta" falsa numa janela modal que oferece as mesmas opções do simulador
// local (número novo, número do app, só a conta, cancelar e erro). O resultado volta para a janela da escola por
// postMessage, como no portal.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../../../data/api-client.js';
import { ApiError } from '../../../data/api-client.js';
import { Button } from '../../../ui/button.js';
import { Icon } from '../../../ui/icon.js';
import { metaSignupMessages } from '../../../../../src/integrations/meta-signup-messages.js';
import {
  EMBEDDED_SIGNUP_CANCEL_STEPS, META_SIGNUP_RESULT_TYPE, isMetaSignupAckMessage, readPassFromHash,
} from '../../../../../shared/meta-signup/embedded-signup-events.js';
import { t } from '../../../i18n/index.js';

const WHATSAPP_MANAGER_URL = 'https://business.facebook.com/wa/manage/home/';
const DEMO_PHONE_NUMBER_ID = '109876543210988';
const DEMO_WABA_ID = '209876543210988';
// como no portal: READY a cada meio segundo, por até 10 s
const HANDSHAKE_INTERVAL_MILLISECONDS = 500;
const HANDSHAKE_ATTEMPTS = 20;

/**
 * @typedef {'LOADING' | 'PASS_INVALID' | 'HANDSHAKE' | 'OPEN_FROM_SCHOOL' | 'READY' | 'META' | 'CONNECTING' | 'SUCCESS' | 'CANCELLED' | 'ERROR' | 'RETRYING'} DemoState
 * @typedef {{ institutionName: string, institutionLogoUrl: string | null, tenantOrigin: string, attemptId: string }} DemoSession
 */

/** @param {string} template @param {Record<string, string>} values */
const format = (template, values) => template.replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match);

/** @param {string} origin */
const hostOf = (origin) => {
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
};

export function MetaSignupDemoPage() {
  const { messages, steps, errors } = metaSignupMessages();
  const [state, setState] = useState(/** @type {DemoState} */ ('LOADING'));
  const [session, setSession] = useState(/** @type {DemoSession | null} */ (null));
  const [mode, setMode] = useState('NEW_NUMBER');
  const [text, setText] = useState('');
  const [result, setResult] = useState(/** @type {any} */ (null));
  const [cancelStep, setCancelStep] = useState(EMBEDDED_SIGNUP_CANCEL_STEPS[0]);
  const passRef = useRef('');
  const proofRef = useRef('');

  /** @param {string} status @param {Record<string, unknown>} [extra] */
  const notify = (status, extra = {}) => {
    if (!session || !window.opener) return false;
    try {
      window.opener.postMessage({ type: META_SIGNUP_RESULT_TYPE, attemptId: session.attemptId, status, ...extra }, session.tenantOrigin);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    document.title = messages.pageTitle;
    // "Tentar de novo": a escola troca só o fragmento por um passe novo, e a página recomeça
    const restartOnNewPass = () => {
      if (readPassFromHash(window.location.hash)) window.location.reload();
    };
    window.addEventListener('hashchange', restartOnNewPass);
    passRef.current = readPassFromHash(window.location.hash);
    window.history.replaceState(null, '', window.location.pathname);
    if (!passRef.current) {
      setState('PASS_INVALID');
      return;
    }
    api.post('/auth/meta-signup/session', { pass: passRef.current })
      .then((body) => {
        setSession(body.data);
        setState(window.opener ? 'HANDSHAKE' : 'OPEN_FROM_SCHOOL');
      })
      .catch(() => setState('PASS_INVALID'));
    return () => window.removeEventListener('hashchange', restartOnNewPass);
  }, []);

  // aperto de mão: só a janela da escola que pediu o passe responde ao READY com a prova; sem resposta, o login não abre
  useEffect(() => {
    if (state !== 'HANDSHAKE' || !session) return undefined;
    let attempts = 0;
    /** @param {MessageEvent} event */
    const handleAck = (event) => {
      if (!isMetaSignupAckMessage(event, { tenantOrigin: session.tenantOrigin, opener: window.opener, attemptId: session.attemptId })) return;
      proofRef.current = event.data.proof;
      setState('READY');
    };
    window.addEventListener('message', handleAck);
    const sendReady = () => {
      if (attempts >= HANDSHAKE_ATTEMPTS || !notify('READY')) {
        setState('OPEN_FROM_SCHOOL');
        return;
      }
      attempts += 1;
    };
    sendReady();
    const interval = setInterval(sendReady, HANDSHAKE_INTERVAL_MILLISECONDS);
    return () => {
      clearInterval(interval);
      window.removeEventListener('message', handleAck);
    };
  }, [state === 'HANDSHAKE' && session !== null]);

  /** @param {{ finishType: string, phoneNumberId?: string, wabaId?: string }} finish */
  const complete = async (finish) => {
    setState('CONNECTING');
    try {
      const body = await api.post('/auth/meta-signup/complete', {
        pass: passRef.current, proof: proofRef.current, code: 'demo-code', mode, finishType: finish.finishType, phoneNumberId: finish.phoneNumberId, wabaId: finish.wabaId,
      });
      const data = body.data;
      setResult(data);
      setState('SUCCESS');
      const hasOpener = notify('CONNECTED', {
        channelId: data.channel?.id ?? null, displayPhoneNumber: data.channel?.displayPhoneNumber ?? '', isCoexistence: Boolean(data.isCoexistence), warnings: data.warnings ?? [],
      });
      if (hasOpener && (data.warnings ?? []).length === 0) setTimeout(() => window.close(), 1500);
    } catch (error) {
      const code = error instanceof ApiError ? error.code : 'UNKNOWN';
      if (code === 'META_SIGNUP_PASS_EXPIRED') {
        setState('PASS_INVALID');
        notify('FAILED', { errorCode: code });
        return;
      }
      setText(errors[code] ?? messages.genericError);
      setState('ERROR');
      notify('FAILED', { errorCode: code });
    }
  };

  /** @param {string | null} step */
  const cancel = (step) => {
    const stepName = step ? steps[step] ?? step : null;
    setText(stepName ? format(messages.cancelledAtStep, { step: stepName }) : messages.cancelled);
    setState('CANCELLED');
    notify('CANCELLED', { cancelledStep: step });
  };

  const metaError = () => {
    setText(format(messages.metaError, { message: 'Este número já está em uso em outra conta', code: '524126' }));
    setState('ERROR');
    notify('FAILED', { errorCode: 'META_ERROR' });
  };

  const retry = () => {
    if (notify('RETRY_REQUESTED')) setState('RETRYING');
    else {
      setText(messages.retryWithoutOpener);
      setState('ERROR');
    }
  };

  const brand = session
    ? html`<div class="meta-demo-brand">
        ${session.institutionLogoUrl
          ? html`<img src=${session.institutionLogoUrl} alt="" />`
          : html`<span class="meta-demo-initial" aria-hidden="true">${session.institutionName.trim().charAt(0).toUpperCase()}</span>`}
        <div>
          <h1>${format(messages.readyTitle, { institution: session.institutionName })}</h1>
          <p class="meta-demo-host">${format(messages.schoolAddress, { host: hostOf(session.tenantOrigin) })}</p>
        </div>
      </div>`
    : null;
  /** @param {'info' | 'success' | 'warning' | 'error'} tone @param {string} message @param {boolean} [isBusy] */
  const status = (tone, message, isBusy = false) => html`<div class="meta-demo-status" data-tone=${tone} role=${tone === 'error' ? 'alert' : 'status'}>
    ${isBusy ? html`<span class="meta-demo-spinner" aria-hidden="true"></span>` : html`<${Icon} name=${tone === 'success' ? 'IconCircleCheck' : tone === 'info' ? 'IconInfoCircle' : 'IconAlertTriangle'} size=${20} />`}
    <p>${message}</p>
  </div>`;
  const closeButton = html`<${Button} title=${messages.close} variant="secondary" size="medium" fullWidth=${true} onClick=${() => window.close()} />`;
  const retryActions = html`<div class="meta-demo-actions meta-demo-actions-row">
    <${Button} title=${messages.retry} icon="IconRefresh" accent="blue" size="medium" fullWidth=${true} onClick=${retry} />
    ${closeButton}
  </div>`;

  /** @param {string} value @param {string} title @param {string} description */
  const choice = (value, title, description) => html`<label class="meta-demo-choice" data-selected=${mode === value ? 'true' : undefined}>
    <input type="radio" name="mode" value=${value} checked=${mode === value} onChange=${() => setMode(value)} />
    <span><span class="meta-demo-choice-title">${title}</span><span class="meta-demo-choice-text">${description}</span></span>
  </label>`;

  let content;
  if (state === 'LOADING') content = status('info', messages.loading, true);
  else if (state === 'PASS_INVALID') content = html`${status('error', messages.passInvalid)}<div class="meta-demo-actions">${closeButton}</div>`;
  else if (state === 'HANDSHAKE') content = html`${brand}${status('info', messages.confirmingSchool, true)}`;
  else if (state === 'OPEN_FROM_SCHOOL') content = html`${brand}${status('warning', messages.openFromSchool)}<div class="meta-demo-actions">${closeButton}</div>`;
  else if (state === 'READY' || state === 'META') {
    content = html`${brand}
      <fieldset class="meta-demo-fieldset">
        <legend>${messages.question}</legend>
        ${choice('NEW_NUMBER', messages.newNumberTitle, messages.newNumberText)}
        ${choice('BUSINESS_APP', messages.businessAppTitle, messages.businessAppText)}
      </fieldset>
      <div class="meta-demo-actions"><${Button} title=${messages.login} icon="IconBrandWhatsapp" accent="blue" size="medium" fullWidth=${true} onClick=${() => setState('META')} /></div>`;
  } else if (state === 'CONNECTING') content = html`${brand}${status('info', messages.connecting, true)}`;
  else if (state === 'RETRYING') content = html`${brand}${status('info', messages.retrying, true)}`;
  else if (state === 'SUCCESS') {
    const number = result?.channel?.displayPhoneNumber ?? '';
    const warnings = result?.warnings ?? [];
    const closesAlone = Boolean(window.opener) && warnings.length === 0;
    content = html`${brand}
      ${status('success', `${messages.successTitle} ${format(messages.success, { number, institution: session?.institutionName ?? '' })}`)}
      <p class="meta-demo-note">${messages.billing} <a href=${WHATSAPP_MANAGER_URL} target="_blank" rel="noopener noreferrer">${messages.billingLink}</a></p>
      <p class="meta-demo-muted">${closesAlone ? messages.closingSoon : messages.closeManually}</p>
      ${closesAlone ? null : html`<div class="meta-demo-actions">${closeButton}</div>`}`;
  } else content = html`${brand}${status(state === 'CANCELLED' ? 'warning' : 'error', text)}${retryActions}`;

  return html`<main class="meta-demo-page">
    <section class="meta-demo-card" aria-live="polite">${content}</section>
    <p class="meta-demo-footer">${messages.poweredBy}</p>
    ${state === 'META'
      ? html`<div class="meta-demo-backdrop">
          <div class="meta-demo-dialog" role="dialog" aria-modal="true" aria-label=${t('metaSignup.demo.title')}>
            <div class="meta-demo-dialog-header">
              <span class="meta-demo-dialog-logo" aria-hidden="true">f</span>
              <div>
                <h2>${t('metaSignup.demo.title')}</h2>
                <p>${t('metaSignup.demo.subtitle')}</p>
              </div>
            </div>
            <div class="meta-demo-dialog-options">
              ${mode === 'NEW_NUMBER'
                ? html`<${Button} title=${t('metaSignup.demo.newNumber')} icon="IconPhonePlus" accent="blue" size="medium" fullWidth=${true}
                    onClick=${() => complete({ finishType: 'FINISH', phoneNumberId: DEMO_PHONE_NUMBER_ID, wabaId: DEMO_WABA_ID })} />`
                : html`<${Button} title=${t('metaSignup.demo.businessApp')} icon="IconBrandWhatsapp" accent="blue" size="medium" fullWidth=${true}
                    onClick=${() => complete({ finishType: 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING', wabaId: DEMO_WABA_ID })} />`}
              <${Button} title=${t('metaSignup.demo.onlyWaba')} variant="secondary" size="medium" fullWidth=${true}
                onClick=${() => complete({ finishType: 'FINISH_ONLY_WABA', wabaId: DEMO_WABA_ID })} />
              <div class="meta-demo-dialog-cancel">
                <select class="text-input" value=${cancelStep} aria-label=${t('metaSignup.demo.cancelAt')}
                  onChange=${(/** @type {Event} */ event) => setCancelStep(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
                  ${EMBEDDED_SIGNUP_CANCEL_STEPS.map((step) => html`<option key=${step} value=${step}>${steps[step]}</option>`)}
                </select>
                <${Button} title=${t('metaSignup.demo.cancelAt')} variant="secondary" size="medium" onClick=${() => cancel(cancelStep)} />
              </div>
              <${Button} title=${t('metaSignup.demo.metaError')} variant="secondary" accent="danger" size="medium" fullWidth=${true} onClick=${metaError} />
              <${Button} title=${t('metaSignup.demo.close')} variant="tertiary" size="medium" fullWidth=${true} onClick=${() => cancel(null)} />
            </div>
          </div>
        </div>`
      : null}
  </main>`;
}
